import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { FREIGHTIQ as T } from "@/constants/testIds/freightiq";
import axios from "axios";
import { CheckCircle2, ArrowRight, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WaitlistDialog = ({ open, onOpenChange, intent = "start_freightiq" }) => {
  const [form, setForm] = useState({
    full_name: "",
    work_email: "",
    company: "",
    role: "",
    monthly_shipments: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const reset = () => {
    setForm({ full_name: "", work_email: "", company: "", role: "", monthly_shipments: "" });
    setSuccess(false);
    setError("");
    setSubmitting(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.full_name || !form.work_email || !form.company) {
      setError("Name, work email and company are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { ...form, intent });
      setSuccess(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        data-testid={T.waitlistDialog}
        className="sm:max-w-[520px] bg-white border border-slate-200 rounded-xl p-0 overflow-hidden"
      >
        {!success ? (
          <form onSubmit={handleSubmit} className="p-8">
            <DialogHeader className="space-y-3 mb-6 text-left">
              <div className="fiq-overline text-[#FF6B35]">FreightIQ™ · Workspace Access</div>
              <DialogTitle className="font-display text-3xl font-bold tracking-tight text-[#0A1128]">
                Create your Freight Workspace
              </DialogTitle>
              <DialogDescription className="text-[#475569] text-sm leading-relaxed">
                Tell us about your operation. We&rsquo;ll provision your private workspace and a coordination specialist will reach out within one business day.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#0A1128]">Full name</Label>
                  <Input
                    data-testid={T.waitlistName}
                    value={form.full_name}
                    onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                    placeholder="Jane Doe"
                    className="border-slate-200 focus-visible:ring-[#0A1128]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-[#0A1128]">Role</Label>
                  <Input
                    data-testid={T.waitlistRole}
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    placeholder="Head of Imports"
                    className="border-slate-200 focus-visible:ring-[#0A1128]"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#0A1128]">Work email</Label>
                <Input
                  data-testid={T.waitlistEmail}
                  type="email"
                  value={form.work_email}
                  onChange={(e) => setForm({ ...form, work_email: e.target.value })}
                  placeholder="jane@company.com"
                  className="border-slate-200 focus-visible:ring-[#0A1128]"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#0A1128]">Company</Label>
                <Input
                  data-testid={T.waitlistCompany}
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  placeholder="Acme Imports Ltd."
                  className="border-slate-200 focus-visible:ring-[#0A1128]"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-semibold text-[#0A1128]">Monthly shipments</Label>
                <Select
                  value={form.monthly_shipments}
                  onValueChange={(v) => setForm({ ...form, monthly_shipments: v })}
                >
                  <SelectTrigger data-testid={T.waitlistVolume} className="border-slate-200">
                    <SelectValue placeholder="Select volume" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1 – 10 containers</SelectItem>
                    <SelectItem value="11-50">11 – 50 containers</SelectItem>
                    <SelectItem value="51-200">51 – 200 containers</SelectItem>
                    <SelectItem value="200+">200+ containers</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {error && (
                <div className="text-xs text-red-600 font-medium border border-red-200 bg-red-50 px-3 py-2 rounded-md">
                  {error}
                </div>
              )}
            </div>

            <DialogFooter className="mt-7 flex-col sm:flex-col gap-2">
              <button
                type="submit"
                disabled={submitting}
                data-testid={T.waitlistSubmit}
                className="fiq-btn-accent w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Provisioning Workspace…
                  </>
                ) : (
                  <>
                    Start FreightIQ™ <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-[11px] text-slate-500 text-center w-full">
                By continuing, you agree to the FreightIQ Workspace terms.
              </p>
            </DialogFooter>
          </form>
        ) : (
          <div data-testid={T.waitlistSuccess} className="p-10 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-5">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="font-display text-2xl font-bold tracking-tight text-[#0A1128] mb-2">
              Your Workspace request is in.
            </h3>
            <p className="text-sm text-[#475569] max-w-sm mx-auto leading-relaxed">
              A FreightIQ coordination specialist will reach out within one business day to provision your private Workspace.
            </p>
            <button
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              className="fiq-btn-primary mt-7 inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-semibold"
            >
              Close
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WaitlistDialog;
